import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, Download, ArrowRight, MapPin } from 'lucide-react';
import BrochureDownloadModal from './BrochureDownloadModal';
import { companyInfo } from '../mockData';

const Footer = () => {
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);

  const handleCatalogueDownload = () => {
    setBrochureModalOpen(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <footer className="bg-[#0A0D0C] text-white relative overflow-hidden">
      {/* Noise Texture */}
      <div className="absolute inset-0 noise-overlay pointer-events-none" />
      
      {/* Gradient Accent */}
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#004534]/20 to-transparent pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-12 py-16 lg:py-20 relative z-10">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Company Info - Enhanced Brand Section */}
          <motion.div variants={itemVariants} className="lg:col-span-1">
            {/* Large Logo */}
            <div className="mb-8">
              <img 
                src="/crilam-logo-oval.png" 
                alt="CriLam Decorative" 
                className="h-24 w-auto object-contain" 
              />
            </div>
            
            {/* Tagline */}
            <p className="text-[#D4AF37] font-medium text-sm mb-4 tracking-wide">
              Premium Manufacturing Excellence
            </p>
            
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Pan-India manufacturer of high-performance acrylic laminated boards and interior doors engineered for durability and consistency.
            </p>
            <div className="flex flex-col gap-3">
              <a 
                href={`tel:${companyInfo.phone}`} 
                className="flex items-center gap-3 text-sm text-white/70 hover:text-[#D4AF37] transition-colors group"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-white/5 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Phone className="h-4 w-4" />
                </div>
                {companyInfo.phone}
              </a>
              <a 
                href={`mailto:${companyInfo.email}`} 
                className="flex items-center gap-3 text-sm text-white/70 hover:text-[#D4AF37] transition-colors group"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-white/5 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Mail className="h-4 w-4" />
                </div>
                <span className="break-all">{companyInfo.email}</span>
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37] mb-6">Quick Links</h3>
            <ul className="flex flex-col gap-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Applications', path: '/applications' },
                { name: 'Dealer / Distributor', path: '/dealer' },
                { name: 'Contact Us', path: '/contact' }
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-sm text-white/60 hover:text-white transition-colors flex items-center group"
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#D4AF37]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* CriLam PUR-Bond™ Technology */}
          <motion.div variants={itemVariants}>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37] mb-6">CriLam PUR-Bond™ Technology</h3>
            <ul className="flex flex-col gap-3">
              {[
                { name: 'What is CriLam PUR-Bond™ Technology?', path: '/pur-technology' },
                { name: 'Why Factory-Laminated?', path: '/why-factory-laminated' },
                { name: 'Benefits by Application', path: '/application-benefits' },
                { name: 'Customer Stories', path: '/customer-stories' },
                { name: 'Ask Your Carpenter', path: '/ask-your-carpenter' }
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-sm text-white/60 hover:text-white transition-colors flex items-center group"
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#D4AF37]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Design Studio Section */}
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37] mb-4 mt-8">Design Studio</h3>
            <ul className="flex flex-col gap-3">
              {[
                { name: '🎨 Design Gallery', path: '/design-gallery' },
                { name: '✨ AI Visualizer', path: '/ai-visualizer' },
                { name: '🎯 Product Recommender', path: '/product-recommender' }
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-sm text-white/60 hover:text-white transition-colors flex items-center group"
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#D4AF37]" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={itemVariants}>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37] mb-6">Our Products</h3>
            <ul className="flex flex-col gap-3">
              {[
                { name: 'Acrylic Laminated Boards', path: '/products/acrylic-boards' },
                { name: 'CNC Designer Doors', path: '/products/cnc-doors' },
                { name: 'Decorative Wall Panels', path: '/products/wall-panels' },
                { name: 'Mica Laminated Doors', path: '/products/mica-doors' },
                { name: 'Veneer Doors', path: '/products/veneer-doors' }
              ].map((product) => (
                <li key={product.path}>
                  <Link 
                    to={product.path} 
                    className="text-sm text-white/60 hover:text-white transition-colors flex items-center group"
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#D4AF37]" />
                    {product.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Resources */}
          <motion.div variants={itemVariants}>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37] mb-6">Resources</h3>
            <ul className="flex flex-col gap-3">
              <li>
                <button
                  onClick={handleCatalogueDownload}
                  className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-2 group"
                  data-testid="footer-download-catalogue"
                >
                  <Download className="h-4 w-4 text-[#D4AF37]" />
                  Download Catalogue
                </button>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-sm text-white/60 hover:text-white transition-colors flex items-center group"
                >
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#D4AF37]" />
                  Request Sample
                </Link>
              </li>
              <li>
                <a
                  href={`https://wa.me/${companyInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/60 hover:text-white transition-colors flex items-center group"
                >
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#D4AF37]" />
                  WhatsApp Us
                </a>
              </li>
            </ul>

            {/* CTA Button */}
            <Link to="/contact" className="inline-block mt-8">
              <motion.button 
                className="px-6 py-3 bg-[#D4AF37] text-[#0A0D0C] text-sm font-medium hover:bg-white transition-colors flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get in Touch
                <ArrowRight className="h-4 w-4" />
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/50">
            © {new Date().getFullYear()} CriLam Decorative Pvt. Ltd. All rights reserved.
          </p>
          <p className="text-xs text-white/30">
            Premium Manufacturer of Acrylic Laminated Boards & Interior Doors
          </p>
        </div>
      </div>
      
      {/* Brochure Download Modal */}
      <BrochureDownloadModal
        isOpen={brochureModalOpen}
        onClose={() => setBrochureModalOpen(false)}
      />
    </footer>
  );
};

export default Footer;
